import {
  useState,
  useCallback,
  useMemo,
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type RefObject,
} from 'react';
import {toast} from '@packages/systems/spring';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {
  ImageUploadService,
  useImageUpload,
} from '@packages/utilities/image-upload/client';
import type {HandleFileInput} from '@packages/utilities/image-upload/client';
import type {ImageUploadResult} from '@packages/utilities/image-upload/shared';
import {generateGuid} from '@packages/utilities/guid';
import {MAX_IMAGE_ASSET_SIZE} from '@packages/shared/constants/Assets';
import {
  ACCEPTED_INPUT_IMAGE_TYPES,
  isAiaOriginalUploadContentType,
} from '@packages/domains/ai-assistant/data-types';
import {extractImageFiles} from '../../../../messages/utils/clipboard';
import type {TagData} from '../context';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_ORIGINAL_UPLOAD_BYTES = MAX_IMAGE_ASSET_SIZE * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2000; // px
const JPEG_MIME_ALIAS = 'image/jpg';

const EXTRA_COMPOSER_IMAGE_TYPES = [
  'image/gif',
  'image/svg+xml',
  'image/avif',
] as const;

const SUPPORTED_IMAGE_MIME_TYPES = new Set<string>(ACCEPTED_INPUT_IMAGE_TYPES);
const ASSET_UPLOAD_IMAGE_MIME_TYPES = new Set<string>([
  ...SUPPORTED_IMAGE_MIME_TYPES,
  ...EXTRA_COMPOSER_IMAGE_TYPES,
]);

export function composerFileInputAccept(assetUploadEnabled: boolean): string {
  if (!assetUploadEnabled) {
    return 'image/*';
  }
  return ['image/*', ...EXTRA_COMPOSER_IMAGE_TYPES].join(',');
}

function unsupportedImageMessage(
  fileName: string,
  assetUploadEnabled: boolean
): string {
  if (assetUploadEnabled) {
    return `${fileName} isn't a supported image. Use PNG, JPEG, WebP, GIF, SVG, or AVIF.`;
  }
  return `${fileName} isn't a supported image. Use PNG, JPEG, or WebP.`;
}

// The picker reports some JPEGs as image/jpg, which the original key map does not list.
function originalUploadContentType(fileType: string): string | null {
  const normalized = fileType === JPEG_MIME_ALIAS ? 'image/jpeg' : fileType;
  return isAiaOriginalUploadContentType(normalized) ? normalized : null;
}

// Resizes proportionately to fit within MAX_IMAGE_DIMENSION and converts to
// WebP. Canvas is created in memory and never mounted to the DOM.
const processImageToWebP = (file: File): Promise<File> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let {naturalWidth: width, naturalHeight: height} = img;
      // An SVG with only a viewBox can report no intrinsic size. Drawn into a
      // square, its preserveAspectRatio keeps its proportions.
      if (!width || !height) {
        width = MAX_IMAGE_DIMENSION;
        height = MAX_IMAGE_DIMENSION;
      }
      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        const scale = Math.min(
          MAX_IMAGE_DIMENSION / width,
          MAX_IMAGE_DIMENSION / height
        );
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error(`Failed to convert ${file.name} to WebP`));
          return;
        }
        const baseName = file.name.replace(/\.[^/.]+$/, '');
        resolve(new File([blob], `${baseName}.webp`, {type: 'image/webp'}));
      }, 'image/webp');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to load ${file.name}`));
    };
    img.src = url;
  });

interface UploadTagState {
  id: string;
  label: string;
  status: 'loading' | 'success' | 'error';
  uploadUrl?: string;
}

interface OriginalUploadRecord {
  originalFileName: string;
  settled: boolean;
  published: boolean;
  key?: string;
  abortController?: AbortController;
}

interface UseFileUploadOptions {
  siteShortName: string;
  /**
   * Optional cap on the total number of attachments allowed in this draft.
   * When set, any `addFiles` call (including paste / file picker) that would
   * push the draft past this number drops the entire batch and invokes
   * `onMaxFilesExceeded`. The hook stays domain-agnostic — what the cap means
   * (model image budget, plan limit, etc.) is the caller's concern.
   */
  maxFiles?: number;
  /** Invoked when a batch is rejected because it would exceed `maxFiles`. */
  onMaxFilesExceeded?: () => void;
}

interface UseFileUploadReturn {
  tags: TagData[];
  uploadedResults: ImageUploadResult[];
  /** Set uploaded results (e.g. restoring draft attachments). */
  mergeUploadedResults: (results: ImageUploadResult[]) => void;
  isUploading: boolean;
  openFilePicker: () => void;
  addFiles: (files: File[]) => Promise<void>;
  clearAll: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  handleFileInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Low-level access for custom upload flows (e.g. Figma paste) where the consumer manages its own tag. */
  handleFileInput: (inputs: HandleFileInput[]) => void;
  /** Extracts images from a clipboard paste event and uploads them. Prevents default if images are found. */
  handlePaste: (e: ClipboardEvent<HTMLTextAreaElement>) => Promise<void>;
}

export const useFileUpload = ({
  siteShortName,
  maxFiles,
  onMaxFilesExceeded,
}: UseFileUploadOptions): UseFileUploadReturn => {
  const assetUploadEnabled = useFeatureFlag(FEATURE_FLAGS.AIA_ASSET_UPLOAD);
  const [tagMap, setTagMap] = useState<Record<string, UploadTagState>>({});
  const [originalUploadsInFlight, setOriginalUploadsInFlight] = useState(0);
  const originalsRef = useRef(new Map<string, OriginalUploadRecord>());
  const previewResultsRef = useRef(new Map<string, ImageUploadResult>());
  const attachedIdsRef = useRef(new Set<string>());
  const publishOriginalRef = useRef<(id: string) => void>(() => {});

  const endpoint = `/api/ai/assistant/${siteShortName}/presigned_url`;
  const uploadService = useMemo(
    () => new ImageUploadService(endpoint),
    [endpoint]
  );

  const updateTagState = useCallback(
    (id: string, updates: Partial<UploadTagState>) => {
      setTagMap((prev) => {
        const existing = prev[id];
        if (!existing) return prev;
        return {...prev, [id]: {...existing, ...updates}};
      });
    },
    []
  );

  const removeTagState = useCallback((id: string) => {
    setTagMap((prev) => {
      const {[id]: _removed, ...rest} = prev;
      return rest;
    });
  }, []);

  const forgetAttachment = useCallback((id: string) => {
    attachedIdsRef.current.delete(id);
    originalsRef.current.get(id)?.abortController?.abort();
    originalsRef.current.delete(id);
    previewResultsRef.current.delete(id);
  }, []);

  const {
    clear,
    cancel,
    fileInputRef,
    openFilePicker,
    uploadedResults,
    mergeUploadedResults,
    handleFileInput,
    uploadMutation,
  } = useImageUpload({
    autoUpload: true,
    endpoint,
    onSuccess: (result: ImageUploadResult) => {
      previewResultsRef.current.set(result.fileId, result);
      updateTagState(result.fileId, {
        status: 'success',
        uploadUrl: result.uploadUrl,
      });
      publishOriginalRef.current(result.fileId);
    },
    onError: (_error, filesToUpload) => {
      for (const file of filesToUpload) {
        forgetAttachment(file.id);
      }
      setTagMap((prev) => {
        const next: Record<string, UploadTagState> = {};
        for (const [id, tag] of Object.entries(prev)) {
          if (tag.status !== 'loading') {
            next[id] = tag;
          }
        }
        return next;
      });
      toast.danger('Failed to upload image');
    },
  });

  const publishOriginal = useCallback(
    (id: string) => {
      if (!attachedIdsRef.current.has(id)) return;
      const record = originalsRef.current.get(id);
      const preview = previewResultsRef.current.get(id);
      if (!record?.settled || !preview || record.published) return;
      record.published = true;
      mergeUploadedResults([
        {
          fileId: preview.fileId,
          uploadUrl: preview.uploadUrl,
          originalFileName: record.originalFileName,
          ...(record.key ? {originalRef: record.key} : {}),
        },
      ]);
    },
    [mergeUploadedResults]
  );
  publishOriginalRef.current = publishOriginal;

  const addFiles = useCallback(
    async (files: File[]) => {
      const acceptedTypes = assetUploadEnabled
        ? ASSET_UPLOAD_IMAGE_MIME_TYPES
        : SUPPORTED_IMAGE_MIME_TYPES;
      const prepared: Array<{id: string; file: File; label: string}> = [];

      const startOriginalUpload = (
        id: string,
        file: File,
        contentType: string
      ) => {
        const controller = new AbortController();
        const record = originalsRef.current.get(id);
        if (record) record.abortController = controller;
        setOriginalUploadsInFlight((count) => count + 1);
        uploadService
          .uploadOriginal(file, contentType, controller.signal)
          .then((key) => {
            const record = originalsRef.current.get(id);
            if (record) record.key = key;
          })
          .catch((error: unknown) => {
            // Aborted when the attachment was forgotten — not a failure.
            if ((error as Error)?.name === 'AbortError') return;
            // A failed original leaves the WebP preview attached with no originalRef.
            console.error('[AI Assistant] Original image upload failed', error);
          })
          .finally(() => {
            const record = originalsRef.current.get(id);
            if (record) {
              record.settled = true;
              record.abortController = undefined;
            }
            setOriginalUploadsInFlight((count) => Math.max(0, count - 1));
            publishOriginalRef.current(id);
          });
      };

      for (const file of files) {
        if (!acceptedTypes.has(file.type)) {
          toast.danger(unsupportedImageMessage(file.name, assetUploadEnabled));
          continue;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
          toast.danger(`${file.name} is ${sizeMB} MB. The maximum is 10 MB`);
          continue;
        }

        const imageId = generateGuid();
        if (assetUploadEnabled) {
          originalsRef.current.set(imageId, {
            originalFileName: file.name,
            settled: false,
            published: false,
          });
          const contentType = originalUploadContentType(file.type);
          if (contentType && file.size <= MAX_ORIGINAL_UPLOAD_BYTES) {
            startOriginalUpload(imageId, file, contentType);
          } else {
            const record = originalsRef.current.get(imageId);
            if (record) record.settled = true;
          }
        }

        let converted: File;
        try {
          converted = await processImageToWebP(file);
        } catch {
          toast.danger(`Failed to process ${file.name}`);
          forgetAttachment(imageId);
          continue;
        }

        prepared.push({id: imageId, file: converted, label: file.name});
      }

      if (prepared.length === 0) return;

      // Reject the whole batch on overflow rather than partially accepting —
      // partial accepts lose track of which files were dropped and confuse
      // paste/drag workflows. The caller decides what `maxFiles` means and
      // owns the user-facing message via `onMaxFilesExceeded`.
      if (maxFiles !== undefined) {
        const currentlyAttached = Object.keys(tagMap).length;
        if (currentlyAttached + prepared.length > maxFiles) {
          for (const item of prepared) {
            forgetAttachment(item.id);
          }
          onMaxFilesExceeded?.();
          return;
        }
      }

      if (assetUploadEnabled) {
        for (const item of prepared) {
          attachedIdsRef.current.add(item.id);
        }
      }

      const inputs: HandleFileInput[] = prepared.map(({id, file, label}) => {
        setTagMap((prev) => ({
          ...prev,
          // Use the original filename as the tag label so the user sees what they selected
          [id]: {
            id,
            label,
            status: 'loading',
          },
        }));
        return {id, file};
      });

      handleFileInput(inputs);
    },
    [
      assetUploadEnabled,
      forgetAttachment,
      handleFileInput,
      maxFiles,
      onMaxFilesExceeded,
      tagMap,
      uploadService,
    ]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      if (selectedFiles.length > 0) {
        addFiles(selectedFiles);
      }
    },
    [addFiles]
  );

  const dismissTag = useCallback(
    (id: string) => {
      forgetAttachment(id);
      cancel(id);
      removeTagState(id);
    },
    [cancel, forgetAttachment, removeTagState]
  );

  const clearAll = useCallback(() => {
    for (const record of originalsRef.current.values()) {
      record.abortController?.abort();
    }
    attachedIdsRef.current.clear();
    originalsRef.current.clear();
    previewResultsRef.current.clear();
    setTagMap({});
    clear();
  }, [clear]);

  const tags = useMemo((): TagData[] => {
    return Object.values(tagMap).map((tag) => ({
      id: tag.id,
      type: 'image',
      label: tag.label,
      status: tag.status === 'loading' ? 'loading' : 'idle',
      imagePreview: tag.uploadUrl,
      onDismiss: () => dismissTag(tag.id),
      onCancel: () => dismissTag(tag.id),
    }));
  }, [tagMap, dismissTag]);

  const handlePaste = useCallback(
    async (e: ClipboardEvent<HTMLTextAreaElement>): Promise<void> => {
      try {
        const imageFiles = await extractImageFiles(e.clipboardData);
        if (imageFiles.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          await addFiles(imageFiles);
        }
      } catch (pasteError) {
        console.error('Error handling paste event:', pasteError);
      }
    },
    [addFiles]
  );

  return {
    tags,
    uploadedResults,
    mergeUploadedResults,
    isUploading: uploadMutation.isLoading || originalUploadsInFlight > 0,
    openFilePicker,
    addFiles,
    clearAll,
    fileInputRef,
    handleFileInputChange,
    handleFileInput,
    handlePaste,
  };
};
